<template>
  <div class="loinc-proposal-form-page">
    <Navbar />
    <PageBanner title="標準提案區" />
    <Breadcrumb :items="breadcrumbItems" />

    <div class="form-container">
      <div class="form-header">
        <h2>臺灣 LOINC 標準提案申請表</h2>
        <p>請依據貴機構之檢驗項目資料，完整填寫下列七大區塊並上傳相關附件證明文件。</p>
      </div>

      <form novalidate @submit.prevent="handleSubmit" class="proposal-form">
        <!-- 一、 基本識別資料 -->
        <section class="form-card">
          <h3 class="card-title">一、 基本識別資料</h3>
          <div class="form-grid">
            <div class="form-group" :class="{ 'has-error': errors.organization }">
              <label>提案機構 <span class="required">*</span></label>
              <input
                v-model.trim="form.organization"
                type="text"
                placeholder="例如：臺北榮民總醫院"
                @input="clearError('organization')"
              />
              <span class="error-text" v-if="errors.organization">{{ errors.organization }}</span>
            </div>

            <div class="form-group" :class="{ 'has-error': errors.laboratory }">
              <label>實驗室 <span class="required">*</span></label>
              <input
                v-model.trim="form.laboratory"
                type="text"
                placeholder="例如：中央檢驗科"
                @input="clearError('laboratory')"
              />
              <span class="error-text" v-if="errors.laboratory">{{ errors.laboratory }}</span>
            </div>

            <div class="form-group" :class="{ 'has-error': errors.contact_name }">
              <label>聯絡人姓名 <span class="required">*</span></label>
              <input
                v-model.trim="form.contact_name"
                type="text"
                placeholder="聯絡人姓名"
                @input="clearError('contact_name')"
              />
              <span class="error-text" v-if="errors.contact_name">{{ errors.contact_name }}</span>
            </div>

            <div class="form-group" :class="{ 'has-error': errors.contact_phone }">
              <label>聯絡人電話 <span class="required">*</span></label>
              <input
                v-model.trim="form.contact_phone"
                type="tel"
                placeholder="02-12345678"
                @input="clearError('contact_phone')"
              />
              <span class="error-text" v-if="errors.contact_phone">{{ errors.contact_phone }}</span>
            </div>

            <div class="form-group" :class="{ 'has-error': errors.contact_email }">
              <label>聯絡人 Email <span class="required">*</span></label>
              <input
                v-model.trim="form.contact_email"
                type="email"
                placeholder="example@hospital.org.tw"
                @input="clearError('contact_email')"
              />
              <span class="error-text" v-if="errors.contact_email">{{ errors.contact_email }}</span>
            </div>

            <div class="form-group" :class="{ 'has-error': errors.hospital_test_code }">
              <label>院內檢驗代碼 <span class="required">*</span></label>
              <input
                v-model.trim="form.hospital_test_code"
                type="text"
                placeholder="例如：LAB-0012"
                @input="clearError('hospital_test_code')"
              />
              <span class="error-text" v-if="errors.hospital_test_code">{{ errors.hospital_test_code }}</span>
            </div>

            <div class="form-group" :class="{ 'has-error': errors.test_name_zh }">
              <label>該檢驗中文名稱 <span class="required">*</span></label>
              <input
                v-model.trim="form.test_name_zh"
                type="text"
                placeholder="例如：血糖測試"
                @input="clearError('test_name_zh')"
              />
              <span class="error-text" v-if="errors.test_name_zh">{{ errors.test_name_zh }}</span>
            </div>

            <div class="form-group" :class="{ 'has-error': errors.test_name_en }">
              <label>該檢驗英文名稱 <span class="required">*</span></label>
              <input
                v-model.trim="form.test_name_en"
                type="text"
                placeholder="例如：Glucose Test"
                @input="clearError('test_name_en')"
              />
              <span class="error-text" v-if="errors.test_name_en">{{ errors.test_name_en }}</span>
            </div>

            <div class="form-group" :class="{ 'has-error': errors.test_department }">
              <label>檢驗科別 <span class="required">*</span></label>
              <input
                v-model.trim="form.test_department"
                type="text"
                placeholder="例如：生化組"
                @input="clearError('test_department')"
              />
              <span class="error-text" v-if="errors.test_department">{{ errors.test_department }}</span>
            </div>

            <div class="form-group" :class="{ 'has-error': errors.item_attribute }">
              <label>項目屬性 <span class="required">*</span></label>
              <select v-model="form.item_attribute" @change="clearError('item_attribute')">
                <option value="" disabled>請選擇項目屬性</option>
                <option value="醫囑項目">醫囑項目</option>
                <option value="結果項目">結果項目</option>
                <option value="兩者皆是">兩者皆是</option>
              </select>
              <span class="error-text" v-if="errors.item_attribute">{{ errors.item_attribute }}</span>
            </div>

            <div class="form-group" :class="{ 'has-error': errors.item_type }">
              <label>項目型態 <span class="required">*</span></label>
              <select v-model="form.item_type" @change="clearError('item_type')">
                <option value="" disabled>請選擇項目型態</option>
                <option value="單項檢驗">單項檢驗</option>
                <option value="計算結果">計算結果</option>
                <option value="Panel">Panel</option>
              </select>
              <span class="error-text" v-if="errors.item_type">{{ errors.item_type }}</span>
            </div>

            <div class="form-group">
              <label>健保代碼或其他國內代碼</label>
              <input v-model.trim="form.nhi_code" type="text" placeholder="若有請填寫" />
            </div>
          </div>
        </section>

        <!-- 二、 提案內容對應至 LOINC 的六軸資料 -->
        <section class="form-card">
          <h3 class="card-title">二、 提案內容對應至 LOINC 的六軸資料</h3>
          <div class="form-grid">
            <div class="form-group full-width" :class="{ 'has-error': errors.axis_component }">
              <label>Component（分析物） <span class="required">*</span></label>
              <textarea
                v-model.trim="form.axis_component"
                rows="2"
                placeholder="實際測量的分析物、抗體、抗原、基因或微生物"
                @input="clearError('axis_component')"
              ></textarea>
              <span class="error-text" v-if="errors.axis_component">{{ errors.axis_component }}</span>
            </div>

            <div class="form-group" :class="{ 'has-error': errors.axis_property }">
              <label>Property（屬性） <span class="required">*</span></label>
              <input
                v-model.trim="form.axis_property"
                type="text"
                placeholder="例如：MCnc, SCnc, PrThr"
                @input="clearError('axis_property')"
              />
              <span class="error-text" v-if="errors.axis_property">{{ errors.axis_property }}</span>
            </div>

            <div class="form-group" :class="{ 'has-error': errors.axis_time_aspect }">
              <label>TimeAspect / Time（時間點） <span class="required">*</span></label>
              <input
                v-model.trim="form.axis_time_aspect"
                type="text"
                placeholder="例如：Pt, 24H"
                @input="clearError('axis_time_aspect')"
              />
              <span class="error-text" v-if="errors.axis_time_aspect">{{ errors.axis_time_aspect }}</span>
            </div>

            <div class="form-group" :class="{ 'has-error': errors.axis_system }">
              <label>System（檢體類型） <span class="required">*</span></label>
              <input
                v-model.trim="form.axis_system"
                type="text"
                placeholder="例如：Serum/Plas, Bld, Urn"
                @input="clearError('axis_system')"
              />
              <span class="error-text" v-if="errors.axis_system">{{ errors.axis_system }}</span>
            </div>

            <div class="form-group" :class="{ 'has-error': errors.axis_scale }">
              <label>Scale（衡量尺度） <span class="required">*</span></label>
              <input
                v-model.trim="form.axis_scale"
                type="text"
                placeholder="例如：Qn, Ord, Nom"
                @input="clearError('axis_scale')"
              />
              <span class="error-text" v-if="errors.axis_scale">{{ errors.axis_scale }}</span>
            </div>

            <div class="form-group">
              <label>Method（方法學）</label>
              <input v-model.trim="form.axis_method" type="text" placeholder="例如：IA, NAA, LC-MS/MS" />
            </div>
          </div>
        </section>

        <!-- 三、 結果語意說明 -->
        <section class="form-card">
          <h3 class="card-title">三、 結果語意說明</h3>
          <div class="form-grid">
            <div class="form-group full-width" :class="{ 'has-error': errors.clinical_description }">
              <label>實際檢驗執行、測量內容及臨床使用說明 <span class="required">*</span></label>
              <textarea
                v-model.trim="form.clinical_description"
                rows="3"
                placeholder="請詳細說明檢驗執行內容與臨床用途"
                @input="clearError('clinical_description')"
              ></textarea>
              <span class="error-text" v-if="errors.clinical_description">{{ errors.clinical_description }}</span>
            </div>

            <div class="form-group full-width" :class="{ 'has-error': errors.result_data_types }">
              <label>結果資料型態（可複選） <span class="required">*</span></label>
              <div class="checkbox-group">
                <label v-for="type in resultDataTypeOptions" :key="type" class="checkbox-label">
                  <input
                    type="checkbox"
                    :value="type"
                    v-model="form.result_data_types"
                    @change="clearError('result_data_types')"
                  />
                  {{ type }}
                </label>
              </div>
              <span class="error-text" v-if="errors.result_data_types">{{ errors.result_data_types }}</span>
            </div>

            <div class="form-group">
              <label>單位（UCUM）</label>
              <input v-model.trim="form.unit" type="text" placeholder="例如：mg/dL" />
            </div>

            <div class="form-group">
              <label>Answer List</label>
              <input v-model.trim="form.answer_list" type="text" placeholder="例如：陽性/陰性" />
            </div>

            <div class="form-group">
              <label>參考區間</label>
              <input v-model.trim="form.reference_range" type="text" placeholder="例如：70 - 99 mg/dL" />
            </div>

            <div class="form-group">
              <label>異常值判定方式</label>
              <input v-model.trim="form.abnormal_criteria" type="text" placeholder="例如：> 100 mg/dL 為異常" />
            </div>

            <div class="form-group">
              <label>極限值設定</label>
              <input v-model.trim="form.extreme_values" type="text" placeholder="偵測極限、報告上限或下限" />
            </div>

            <div class="form-group">
              <label>是否為計算值</label>
              <select v-model="form.is_calculated">
                <option :value="false">否</option>
                <option :value="true">是</option>
              </select>
            </div>

            <div class="form-group full-width" v-if="form.is_calculated">
              <label>計算公式</label>
              <textarea v-model.trim="form.calculation_formula" rows="2" placeholder="請填寫計算公式"></textarea>
            </div>
          </div>
        </section>

        <!-- 四、 檢驗技術說明 -->
        <section class="form-card">
          <h3 class="card-title">四、 檢驗技術說明</h3>
          <div class="form-grid">
            <div class="form-group">
              <label>儀器廠牌及型號</label>
              <input v-model.trim="form.instrument_brand_model" type="text" placeholder="儀器名稱與型號" />
            </div>

            <div class="form-group">
              <label>試劑名稱、型號及製造商</label>
              <input v-model.trim="form.reagent_info" type="text" placeholder="試劑品牌及型號" />
            </div>

            <div class="form-group full-width">
              <label>方法學原理</label>
              <textarea v-model.trim="form.method_principle" rows="2" placeholder="簡述方法學原理"></textarea>
            </div>

            <div class="form-group full-width">
              <label>檢體採集與前處理方式</label>
              <textarea v-model.trim="form.specimen_collection" rows="2" placeholder="採集與前處理條件"></textarea>
            </div>

            <div class="form-group">
              <label>結果可比較性</label>
              <input v-model.trim="form.result_comparability" type="text" placeholder="不同方法是否影響可比較性" />
            </div>

            <div class="form-group">
              <label>同業使用狀況</label>
              <input v-model.trim="form.peer_usage" type="text" placeholder="是否有其他醫院使用同項目" />
            </div>
          </div>
        </section>

        <!-- 五、 候選 LOINC 分析 -->
        <section class="form-card">
          <h3 class="card-title">五、 候選 LOINC 分析</h3>
          <div class="form-grid">
            <div class="form-group">
              <label>候選 LOINC Code</label>
              <input v-model.trim="form.candidate_loinc_code" type="text" placeholder="例如：2345-7" />
            </div>

            <div class="form-group">
              <label>候選 LOINC Fully-Specified Name</label>
              <input v-model.trim="form.candidate_loinc_name" type="text" placeholder="LOINC 全名" />
            </div>

            <div class="form-group full-width">
              <label>每個六軸的比較</label>
              <textarea v-model.trim="form.axis_comparison" rows="2" placeholder="六軸對照比較說明"></textarea>
            </div>

            <div class="form-group full-width">
              <label>支持選用的理由</label>
              <textarea v-model.trim="form.selection_rationale" rows="2" placeholder="推薦說明理由"></textarea>
            </div>

            <div class="form-group">
              <label>對應信心程度</label>
              <input v-model.trim="form.confidence_level" type="text" placeholder="高 / 中 / 低" />
            </div>

            <div class="form-group">
              <label>對應關係判定</label>
              <select v-model="form.mapping_relationship">
                <option value="">請選擇對應關係</option>
                <option value="Exact">Exact</option>
                <option value="Conditional">Conditional</option>
                <option value="Narrower">Narrower</option>
                <option value="Broader">Broader</option>
                <option value="Inexact">Inexact</option>
              </select>
            </div>
          </div>
        </section>

        <!-- 六、 附件上傳區 -->
        <section class="form-card">
          <h3 class="card-title">六、 附件檔案上傳區</h3>
          <div class="form-grid">
            <div class="form-group full-width" :class="{ 'has-error': errors.reagent_document }">
              <label>
                試劑仿單或檢驗操作文件（必填，格式：PDF / DOCX / DOC / JPG / PNG，大小 10MB 內）
                <span class="required">*</span>
              </label>
              <div class="file-input-wrapper">
                <input
                  type="file"
                  @change="onFileChange($event, 'reagent_document')"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
              </div>
              <span v-if="files.reagent_document" class="file-name">已選擇檔案: {{ files.reagent_document.name }} ({{ formatFileSize(files.reagent_document.size) }})</span>
              <span class="error-text" v-if="errors.reagent_document">{{ errors.reagent_document }}</span>
            </div>

            <div class="form-group full-width" :class="{ 'has-error': errors.deidentified_report }">
              <label>
                實際去識別化報告範例（必填，格式：PDF / DOCX / DOC / JPG / PNG / TXT，大小 10MB 內）
                <span class="required">*</span>
              </label>
              <div class="file-input-wrapper">
                <input
                  type="file"
                  @change="onFileChange($event, 'deidentified_report')"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                />
              </div>
              <span v-if="files.deidentified_report" class="file-name">已選擇檔案: {{ files.deidentified_report.name }} ({{ formatFileSize(files.deidentified_report.size) }})</span>
              <span class="error-text" v-if="errors.deidentified_report">{{ errors.deidentified_report }}</span>
            </div>

            <div class="form-group full-width" :class="{ 'has-error': errors.supplementary_document }">
              <label>
                其他補充說明文件（選填，格式：PDF / DOCX / DOC / JPG / PNG / TXT，大小 10MB 內）
              </label>
              <div class="file-input-wrapper">
                <input
                  type="file"
                  @change="onFileChange($event, 'supplementary_document')"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                />
              </div>
              <span v-if="files.supplementary_document" class="file-name">已選擇檔案: {{ files.supplementary_document.name }} ({{ formatFileSize(files.supplementary_document.size) }})</span>
              <span class="error-text" v-if="errors.supplementary_document">{{ errors.supplementary_document }}</span>
            </div>
          </div>
        </section>

        <!-- 送出按鈕 -->
        <div class="form-actions">
          <router-link to="/standard-proposals" class="btn-cancel">取消返回</router-link>
          <button type="submit" class="btn-submit" :disabled="submitting">
            <span v-if="submitting">送出中...</span>
            <span v-else>確認送出提案</span>
          </button>
        </div>
      </form>
    </div>

    <FooterSection />
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { message } from 'ant-design-vue';
import Navbar from '@/components/Navbar.vue';
import PageBanner from '@/components/PageBanner.vue';
import Breadcrumb from '@/components/Breadcrumb.vue';
import FooterSection from '@/components/FooterSection.vue';
import { loincProposalAPI, authAPI } from '@/services/api';

const router = useRouter();
const submitting = ref(false);

const breadcrumbItems = [
  { label: '首頁', path: '/' },
  { label: '臺灣標準' },
  { label: '標準提案區', path: '/standard-proposals' },
  { label: '填寫提案表單' },
];

const resultDataTypeOptions = ['數值', '陽陰性', '菌名', '文字報告', '序列資料'];

const fieldLabels = {
  organization: '提案機構',
  laboratory: '實驗室',
  contact_name: '聯絡人姓名',
  contact_phone: '聯絡人電話',
  contact_email: '聯絡人 Email',
  hospital_test_code: '院內檢驗代碼',
  test_name_zh: '該檢驗中文名稱',
  test_name_en: '該檢驗英文名稱',
  test_department: '檢驗科別',
  item_attribute: '項目屬性',
  item_type: '項目型態',
  axis_component: 'Component（分析物）',
  axis_property: 'Property（屬性）',
  axis_time_aspect: 'TimeAspect / Time（時間點）',
  axis_system: 'System（檢體類型）',
  axis_scale: 'Scale（衡量尺度）',
  clinical_description: '實際檢驗執行與臨床使用說明',
  result_data_types: '結果資料型態',
  reagent_document: '試劑仿單或檢驗操作文件',
  deidentified_report: '實際去識別化報告範例',
  supplementary_document: '其他補充說明文件',
};

const allowedFileTypes = {
  reagent_document: ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'],
  deidentified_report: ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png', 'txt'],
  supplementary_document: ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png', 'txt'],
};

const form = reactive({
  organization: '',
  laboratory: '',
  contact_name: '',
  contact_phone: '',
  contact_email: '',
  hospital_test_code: '',
  test_name_zh: '',
  test_name_en: '',
  test_department: '',
  item_attribute: '',
  item_type: '',
  nhi_code: '',
  axis_component: '',
  axis_property: '',
  axis_time_aspect: '',
  axis_system: '',
  axis_scale: '',
  axis_method: '',
  clinical_description: '',
  result_data_types: [],
  unit: '',
  answer_list: '',
  reference_range: '',
  abnormal_criteria: '',
  extreme_values: '',
  is_calculated: false,
  calculation_formula: '',
  instrument_brand_model: '',
  reagent_info: '',
  method_principle: '',
  specimen_collection: '',
  result_comparability: '',
  peer_usage: '',
  candidate_loinc_code: '',
  candidate_loinc_name: '',
  axis_comparison: '',
  selection_rationale: '',
  confidence_level: '',
  mapping_relationship: '',
});

const files = reactive({
  reagent_document: null,
  deidentified_report: null,
  supplementary_document: null,
});

const errors = reactive({});

const clearError = (fieldKey) => {
  if (errors[fieldKey]) {
    delete errors[fieldKey];
  }
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

onMounted(async () => {
  const token = localStorage.getItem('ms_platform_token');
  if (!token) {
    message.warning('請先登入後再進行提案');
    router.push('/sign-in?redirect=/loinc-proposal-form');
    return;
  }

  try {
    const res = await authAPI.me();
    if (res.data) {
      const isAgreed = res.data.standards_terms_agreed ? res.data.standards_terms_agreed['loinc'] : res.data.additional_terms_agreed;
      if (!isAgreed) {
        message.warning('請先閱讀並同意最新版附加條款後再進行提案');
        router.push({
          path: '/additional-terms',
          query: { redirect: '/loinc-proposal-form' },
        });
        return;
      }
      localStorage.setItem('user_data', JSON.stringify(res.data));

      if (!form.contact_name) form.contact_name = res.data.name || '';
      if (!form.contact_email) form.contact_email = res.data.email || '';
      if (!form.contact_phone) form.contact_phone = res.data.phone || '';
      if (!form.organization) form.organization = res.data.organization || '';
    }
  } catch (err) {
    if (err?.response?.status === 401) {
      message.warning('登入逾期，請重新登入');
      router.push('/sign-in?redirect=/loinc-proposal-form');
    }
  }
});

const onFileChange = (event, fieldKey) => {
  const file = event.target.files[0];
  const label = fieldLabels[fieldKey] || fieldKey;

  if (!file) {
    files[fieldKey] = null;
    if (fieldKey !== 'supplementary_document') {
      errors[fieldKey] = `請選擇${label}`;
    }
    return;
  }

  const fileNameParts = file.name.split('.');
  const ext = fileNameParts.length > 1 ? fileNameParts.pop().toLowerCase() : '';
  const allowed = allowedFileTypes[fieldKey] || [];

  if (!allowed.includes(ext)) {
    const msg = `檔案格式不符合（目前：.${ext}，僅支援：${allowed.map((e) => '.' + e).join(', ')}）`;
    errors[fieldKey] = msg;
    files[fieldKey] = null;
    event.target.value = '';
    message.error(`【${label}】${msg}`);
    return;
  }

  if (file.size > 10 * 1024 * 1024) {
    const msg = `檔案大小不可超過 10MB（目前：${(file.size / (1024 * 1024)).toFixed(2)} MB）`;
    errors[fieldKey] = msg;
    files[fieldKey] = null;
    event.target.value = '';
    message.error(`【${label}】${msg}`);
    return;
  }

  delete errors[fieldKey];
  files[fieldKey] = file;
};

const scrollToFirstError = () => {
  setTimeout(() => {
    const firstErrorEl = document.querySelector('.has-error');
    if (firstErrorEl) {
      firstErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const inputEl = firstErrorEl.querySelector('input, select, textarea');
      if (inputEl) {
        inputEl.focus({ preventScroll: true });
      }
    }
  }, 100);
};

const validateForm = () => {
  Object.keys(errors).forEach((key) => delete errors[key]);

  const requiredTextFields = [
    'organization',
    'laboratory',
    'contact_name',
    'contact_phone',
    'contact_email',
    'hospital_test_code',
    'test_name_zh',
    'test_name_en',
    'test_department',
    'item_attribute',
    'item_type',
    'axis_component',
    'axis_property',
    'axis_time_aspect',
    'axis_system',
    'axis_scale',
    'clinical_description',
  ];

  requiredTextFields.forEach((key) => {
    if (!form[key] || !String(form[key]).trim()) {
      errors[key] = `請填寫${fieldLabels[key]}`;
    }
  });

  if (form.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email)) {
    errors.contact_email = '請輸入正確的 Email 格式';
  }

  if (!form.result_data_types || form.result_data_types.length === 0) {
    errors.result_data_types = '請至少選擇一種結果資料型態';
  }

  if (!files.reagent_document) {
    errors.reagent_document = '請選擇並上傳試劑仿單或檢驗操作文件';
  }

  if (!files.deidentified_report) {
    errors.deidentified_report = '請選擇並上傳實際去識別化報告範例';
  }

  const errKeys = Object.keys(errors);
  if (errKeys.length > 0) {
    const errLabels = errKeys.map((k) => fieldLabels[k] || k);
    message.error(`請修正以下 ${errKeys.length} 個欄位：${errLabels.join('、')}`);
    scrollToFirstError();
    return false;
  }

  return true;
};

const handleSubmit = async () => {
  if (!validateForm()) {
    return;
  }

  submitting.value = true;

  try {
    const formData = new FormData();
    Object.keys(form).forEach((key) => {
      if (key === 'is_calculated') {
        // 傳送 '1' 或 '0' 確保 Laravel 驗證與 Eloquent 解析為正確布林值
        formData.append('is_calculated', form.is_calculated ? '1' : '0');
      } else if (Array.isArray(form[key])) {
        form[key].forEach((val) => formData.append(`${key}[]`, val));
      } else if (form[key] !== null && form[key] !== undefined) {
        formData.append(key, form[key]);
      }
    });

    if (files.reagent_document) {
      formData.append('reagent_document', files.reagent_document);
    }
    if (files.deidentified_report) {
      formData.append('deidentified_report', files.deidentified_report);
    }
    if (files.supplementary_document) {
      formData.append('supplementary_document', files.supplementary_document);
    }

    await loincProposalAPI.submit(formData);

    message.success('提案申請已成功送出！');
    router.push('/dashboard');
  } catch (error) {
    if (error.response && error.response.status === 422) {
      const backendErrors = error.response.data?.content?.errors || {};
      const errKeys = Object.keys(backendErrors);
      if (errKeys.length > 0) {
        const errLabels = [];
        errKeys.forEach((key) => {
          const label = fieldLabels[key] || key;
          const msgList = backendErrors[key];
          const msgStr = Array.isArray(msgList) ? msgList.join(' ') : String(msgList);
          errors[key] = `${label}：${msgStr}`;
          errLabels.push(label);
        });
        message.error(`後端驗證失敗，請檢查以下欄位：${errLabels.join('、')}`);
        scrollToFirstError();
        return;
      }
    }
    const msg = error.response?.data?.message || '提案送出失敗，請稍後再試';
    if (error.response?.status === 403 && (error.response?.data?.content?.code === 'ADDITIONAL_TERMS_REQUIRED' || msg.includes('附加條款'))) {
      message.warning('請先閱讀並同意最新版附加條款後再進行提案');
      router.push({
        path: '/additional-terms',
        query: { redirect: '/loinc-proposal-form' },
      });
      return;
    }
    message.error(msg);
  } finally {
    submitting.value = false;
  }
};
</script>

<style scoped lang="scss">
.loinc-proposal-form-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
}

.form-container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 40px 20px 80px;
  width: 100%;
}

.form-header {
  margin-bottom: 32px;

  h2 {
    font-size: 26px;
    font-weight: 700;
    color: #022964;
    margin-bottom: 8px;
  }

  p {
    font-size: 15px;
    color: #666;
  }
}

.proposal-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-card {
  background: #fff;
  border-radius: 8px;
  padding: 28px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  .card-title {
    font-size: 18px;
    font-weight: 700;
    color: #0079bb;
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 2px solid #eef2f5;
  }
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;

  &.full-width {
    grid-column: 1 / -1;
  }

  label {
    font-size: 14px;
    font-weight: 600;
    color: #333;

    .required {
      color: #e53935;
    }
  }

  input[type='text'],
  input[type='email'],
  input[type='tel'],
  select,
  textarea {
    width: 100%;
    padding: 10px 14px;
    border: 1.5px solid #dcdfe6;
    border-radius: 6px;
    font-size: 14px;
    color: #333;
    background: #fff;
    transition: all 0.2s ease;

    &:focus {
      outline: none;
      border-color: #0079bb;
    }
  }

  /* 出錯紅框與高亮樣式 */
  &.has-error {
    label {
      color: #e53935;
    }

    input[type='text'],
    input[type='email'],
    input[type='tel'],
    select,
    textarea {
      border-color: #e53935 !important;
      background-color: #fff8f8 !important;
      box-shadow: 0 0 0 3px rgba(229, 57, 53, 0.12) !important;
    }

    .checkbox-group {
      padding: 10px;
      border: 1.5px solid #e53935;
      border-radius: 6px;
      background-color: #fff8f8;
    }

    .file-input-wrapper {
      padding: 10px;
      border: 1.5px dashed #e53935;
      border-radius: 6px;
      background-color: #fff8f8;
    }
  }

  .error-text {
    font-size: 13px;
    color: #e53935;
    margin-top: 2px;
    font-weight: 500;
  }

  .file-input-wrapper {
    display: flex;
    align-items: center;

    input[type='file'] {
      font-size: 14px;
      cursor: pointer;
    }
  }

  .file-name {
    font-size: 13px;
    color: #2e7d32;
    margin-top: 4px;
    font-weight: 500;
  }
}

.checkbox-group {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 4px;
  transition: all 0.2s ease;

  .checkbox-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    cursor: pointer;
  }
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 16px;
}

.btn-cancel {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 28px;
  border: 1.5px solid #ccc;
  border-radius: 50px;
  color: #666;
  text-decoration: none;
  font-size: 15px;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    background: #eee;
  }
}

.btn-submit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 36px;
  background: var(--color-primary, #0079bb);
  color: #fff;
  border: none;
  border-radius: 50px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: var(--color-primary-hover, #005f96);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
</style>
